import { useCallback, useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  Configuration,
  ShipTrackIdGetRequest,
  TrackApi,
  TrackPost,
  type TrackResult,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const useTracksApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}/aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const portserverApi = useMemo(
    () => new TrackApi(configuration),
    [configuration]
  );
  return apiClientWrapper(portserverApi) as TrackApi;
};

export const useTracksGetList = (
  enabled = true
): UseQueryResult<{
  records?: Array<TrackResult>;
}> => {
  const tracksApi = useTracksApi();

  return useQuery(
    ['trackListGet'],
    async () => {
      return await tracksApi.trackGet();
    },
    {
      enabled: enabled,
      refetchInterval: 30000, // 30 seconds in milliseconds
      refetchIntervalInBackground: true, // Continue polling even when tab is not active
    }
  );
};

export const useCreateTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    (route: TrackPost) => {
      return tracksApi.shipTrackPost({ json: route });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['trackListGet']);
        queryClient.invalidateQueries(['arrivals']);
      },
    }
  );
};

export const useUpdateTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, route }: { id: number; route: TrackPost }) => {
      return tracksApi.shipTrackIdPut({ id, json: route });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['trackListGet']),
    }
  );
};

export const useDeleteTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    (id: number) => {
      return tracksApi.shipTrackIdDelete({ id });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['trackListGet']);
        queryClient.invalidateQueries(['arrivals']);
      },
    }
  );
};

export const useFetchTrackById = () => {
  const tracksApi = useTracksApi();

  return useCallback(
    async (requestParameters: ShipTrackIdGetRequest) => {
      return await tracksApi.shipTrackIdGet(requestParameters);
    },
    [tracksApi]
  );
};
